import { EnvironmentProviders, isDevMode, LOCALE_ID, makeEnvironmentProviders, signal, Signal } from '@angular/core';
import { provideTransloco } from '@jsverse/transloco';
import { provideTranslocoMessageformat } from '@jsverse/transloco-messageformat';
import { provideTranslocoPersistLang } from '@jsverse/transloco-persist-lang';
import { TUI_DATE_FORMAT, TUI_DEFAULT_DATE_FORMAT, TuiDateFormatSettings } from '@taiga-ui/core';
import { TUI_ENGLISH_LANGUAGE, TUI_LANGUAGE, TuiLanguage } from '@taiga-ui/i18n';
import { DEFAULT_LOCALE_CODE, TRANSLOCO_LANG_STORAGE_KEY } from 'transloco.config';
import { availableLocaleCodes } from './config/available-locale-codes';
import { availableLocales } from './config/available-locales';
import { DATE_FNS_FORMAT, DEFAULT_DATE_FORMAT } from './date-format.provider';
import { providePreloadLang } from './preload-lang-provider';
import { TranslocoHttpLoader } from './transloco-loader';
import { resolveActiveLocale } from './utils/resolve-locale';

const getAngularLocale = (): string => {
  const cachedLang = localStorage.getItem(TRANSLOCO_LANG_STORAGE_KEY);
  const cultureLang = navigator.language;
  return resolveActiveLocale({
    cachedLang,
    browserLang: cultureLang?.split('-')[0],
    cultureLang,
    defaultLang: DEFAULT_LOCALE_CODE,
  });
};

const getTuiLanguage = (): Signal<TuiLanguage> => {
  const locale = availableLocales.find((l) => l.code === getAngularLocale());
  return signal(locale?.tuiConfig.language || TUI_ENGLISH_LANGUAGE);
};

const getTuiDateFormat = (): Signal<TuiDateFormatSettings> => {
  const locale = availableLocales.find((l) => l.code === getAngularLocale());
  return signal(locale?.tuiConfig.format || TUI_DEFAULT_DATE_FORMAT);
};

const getDateFnsFormat = (): string => {
  const locale = availableLocales.find((l) => l.code === getAngularLocale());
  return locale?.dateFnsConfig.format || DEFAULT_DATE_FORMAT;
};

export const provideTranslocoConfig = (): EnvironmentProviders =>
  makeEnvironmentProviders([
    provideTransloco({
      config: {
        availableLangs: availableLocaleCodes,
        defaultLang: DEFAULT_LOCALE_CODE,
        reRenderOnLangChange: false,
        prodMode: !isDevMode(),
        flatten: {
          aot: !isDevMode(),
        },
      },
      loader: TranslocoHttpLoader,
    }),
    provideTranslocoPersistLang({
      getLangFn: resolveActiveLocale,
      storage: {
        useValue: localStorage,
      },
    }),
    providePreloadLang(),
    provideTranslocoMessageformat({
      locales: availableLocaleCodes,
    }),
    {
      provide: LOCALE_ID,
      useFactory: getAngularLocale,
    },
    {
      provide: TUI_LANGUAGE,
      useFactory: getTuiLanguage,
    },
    {
      provide: TUI_DATE_FORMAT,
      useFactory: getTuiDateFormat,
    },
    {
      provide: DATE_FNS_FORMAT,
      useFactory: getDateFnsFormat,
    },
  ]);
